/**
 * `SidebarBrand` — the brand header row at the top of the new Claude-
 * style `Sidebar`. Composes:
 *
 *   - Always: the small `LexiunMark` glyph (gold in dark, coffee-900
 *     in light — matching the rest of the welcome).
 *   - When expanded: the `LexiunWordmark` next to the glyph + the org
 *     name on a second line (from `useMe().data.org.name`, same source
 *     the retired `Header.tsx` used).
 *   - When collapsed: only the glyph is visible; wordmark + org name
 *     fade out via the parent sidebar's opacity transition.
 *
 * This is layout-only. The toggle button (`sidebar-toggle` testid) is
 * owned by the `Sidebar` itself so its position can flip between
 * top-right (expanded) and top-center (collapsed) without restructuring
 * the brand block.
 */

import { useMe } from "@/hooks/useMe";
import { LexiunMark } from "@/components/home/LexiunMark";
import { LexiunWordmark } from "@/components/layout/LexiunWordmark";
import { cn } from "@/lib/utils";

interface SidebarBrandProps {
  expanded: boolean;
}

export function SidebarBrand({ expanded }: SidebarBrandProps) {
  const me = useMe();
  const orgName = me.data?.org?.name ?? null;

  // Claude-style collapsed mode: the brand block is hidden entirely.
  // Only the sidebar-toggle button sits at the top of the rail; the
  // LexiunMark glyph + wordmark + org name reappear together as soon
  // as the sidebar expands.
  if (!expanded) return null;

  return (
    <div
      data-testid="sidebar-brand"
      data-expanded={expanded}
      className="flex min-w-0 items-center gap-2 px-3 pt-3 text-coffee-900 dark:text-gold-400"
    >
      <LexiunMark aria-hidden className="h-5 w-5 shrink-0" />
      <div className="flex min-w-0 flex-col">
        <LexiunWordmark className="h-5 w-auto" />
        {orgName && (
          <span className="mt-0.5 truncate text-caption text-coffee-600 dark:text-cream-400">
            {orgName}
          </span>
        )}
      </div>
    </div>
  );
}
